package br.com.vvrefeicoes.portal.domain.model

import org.jetbrains.compose.resources.StringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.orgchart_status_current
import portalvv.shared.generated.resources.orgchart_status_draft
import portalvv.shared.generated.resources.orgchart_status_legacy

enum class OrgChartStatus(
    val label: StringResource,
) {
    Current(Res.string.orgchart_status_current),
    Draft(Res.string.orgchart_status_draft),
    Legacy(Res.string.orgchart_status_legacy),
}

enum class CompanyType {
    UPR,
    TRA,
    LOJ,
    EVENTOS,
}

const val DEFAULT_ADMIN_OTHERS_PERCENT = 7.0

data class Company(
    val id: String,
    val name: String,
    val type: CompanyType,
    val adminOthersPercent: Double = DEFAULT_ADMIN_OTHERS_PERCENT,
    val legalName: String = "",
    val logo: String = "",
    val email: String = "",
    val login: String = "",
    /** Senha somente de formulário; nunca vem da API nem é persistida no estado remoto. */
    val pendingPassword: String = "",
    val commercialPhone: String = "",
    val mobilePhone: String = "",
    val cnpj: String = "",
    val employeeCount: Int = 0,
    val cep: String = "",
    val street: String = "",
    val number: String = "",
    val neighborhood: String = "",
    val city: String = "",
    val state: String = "",
    val leftoverMeals: Int = 0,
    val line: String = "0",
    val buttonCount: Int = 3,
    val shortAutoSendTime: Int = 2,
    val longAutoSendTime: Int = 45,
    val commentAllRatings: Boolean = false,
    val directorNames: List<String> = emptyList(),
    val managerNames: List<String> = emptyList(),
    val updatedBy: String = "",
    val updatedAt: String = "",
    val inactive: Boolean = false,
    val organogramaUnidadeId: String = "",
)

data class CompanyMissingSyncResult(
    val companiesCreated: Int,
    val companiesAlreadyExisting: Int,
    val servicesCreated: Int,
    val servicesAlreadyExisting: Int,
    val linesCreated: Int,
    val associationsCreated: Int,
)

data class CostCenter(
    val id: String,
    val companyId: String,
    val name: String,
    val code: String = "",
    val source: String = "MANUAL",
    val active: Boolean = true,
)

data class CompanyService(
    val id: String,
    val companyId: String,
    val serviceType: String = "",
    val name: String,
    val startTime: String = "",
    val endTime: String = "",
    val price: Double = 0.0,
    val active: Boolean = true,
    val dailyQuantity: Int = 0,
    val quantity: Int = 0,
    val mondayQuantity: Int = 0,
    val tuesdayQuantity: Int = 0,
    val wednesdayQuantity: Int = 0,
    val thursdayQuantity: Int = 0,
    val fridayQuantity: Int = 0,
    val saturdayQuantity: Int = 0,
    val sundayQuantity: Int = 0,
    val sundayActive: Boolean = false,
    val activeDays: Map<String, List<Int>> = emptyMap(),
    val lines: List<Line> = emptyList(),
)

data class Line(
    val id: String,
    val code: String,
    val name: String,
    val active: Boolean = true,
    val displayOrder: Int = 0,
    val isDefault: Boolean = false,
)

data class ServiceTypeOption(
    val id: String,
    val name: String,
)

data class CompanyServiceDraft(
    val serviceType: String,
    val name: String,
    val startTime: String,
    val endTime: String,
    val price: String = "",
    val quantity: Int = 0,
    val mondayQuantity: Int = 0,
    val tuesdayQuantity: Int = 0,
    val wednesdayQuantity: Int = 0,
    val thursdayQuantity: Int = 0,
    val fridayQuantity: Int = 0,
    val saturdayQuantity: Int = 0,
    val sundayQuantity: Int = 0,
    val sundayActive: Boolean = false,
    val dailyQuantity: Int = 0,
    val activeDays: Map<String, List<Int>> = emptyMap(),
    val lineIds: List<String> = emptyList(),
)

data class Manager(
    val id: String,
    val name: String,
    val inactive: Boolean = false,
)

data class Director(
    val id: String,
    val name: String,
    val inactive: Boolean = false,
)

data class Supervisor(
    val id: String,
    val name: String,
    val inactive: Boolean = false,
    val cargo: String = "",
)

data class CompanyLink(
    val directorId: String,
    val managerId: String?,
)

data class OrgChartData(
    val directors: List<Director> = emptyList(),
    val managers: List<Manager> = emptyList(),
    val supervisors: List<Supervisor> = emptyList(),
    val companies: List<Company> = emptyList(),
    val managerDirectors: Map<String, Set<String>> = emptyMap(),
    val companyLinks: Map<String, Set<CompanyLink>> = emptyMap(),
    val unitSupervisors: Map<String, Set<String>> = emptyMap(),
) {
    fun deepCopy(): OrgChartData =
        copy(
            directors = directors.toList(),
            managers = managers.toList(),
            supervisors = supervisors.toList(),
            companies = companies.toList(),
            managerDirectors = managerDirectors.mapValues { it.value.toSet() },
            companyLinks = companyLinks.mapValues { it.value.toSet() },
            unitSupervisors = unitSupervisors.mapValues { it.value.toSet() },
        )
}

data class OrgChartVersion(
    val id: String,
    val number: String,
    val status: OrgChartStatus,
    val publishedAt: String? = null,
    val editedAt: String? = null,
    val effectiveFrom: String? = null,
    val effectiveTo: String? = null,
    val owner: String? = null,
    val data: OrgChartData = OrgChartData(),
)

internal fun String.orgChartSortKey(): String =
    lowercase()
        .map { char ->
            when (char) {
                'á', 'à', 'â', 'ã', 'ä' -> 'a'
                'é', 'è', 'ê', 'ë' -> 'e'
                'í', 'ì', 'î', 'ï' -> 'i'
                'ó', 'ò', 'ô', 'õ', 'ö' -> 'o'
                'ú', 'ù', 'û', 'ü' -> 'u'
                'ç' -> 'c'
                'ñ' -> 'n'
                else -> char
            }
        }.joinToString("")

fun OrgChartData.managersOfDirector(directorId: String): List<Manager> =
    managers
        .filter { manager -> !manager.inactive && directorId in (managerDirectors[manager.id] ?: emptySet()) }
        .sortedBy { it.name.orgChartSortKey() }

fun OrgChartData.companiesOfManager(
    managerId: String,
    directorId: String,
): List<Company> =
    companies
        .filter { company ->
            !company.inactive && CompanyLink(directorId, managerId) in (companyLinks[company.id] ?: emptySet())
        }.sortedBy { it.name.orgChartSortKey() }

fun OrgChartData.companiesOfManager(managerId: String): List<Company> =
    companies
        .filter { company ->
            !company.inactive && (companyLinks[company.id] ?: emptySet()).any { it.managerId == managerId }
        }.sortedBy { it.name.orgChartSortKey() }

fun OrgChartData.directCompaniesOfDirector(directorId: String): List<Company> =
    companies
        .filter { company ->
            !company.inactive && CompanyLink(directorId, null) in (companyLinks[company.id] ?: emptySet())
        }.sortedBy { it.name.orgChartSortKey() }

fun OrgChartData.managersWithoutDirector(): List<Manager> =
    managers
        .filter { manager -> !manager.inactive && (managerDirectors[manager.id] ?: emptySet()).isEmpty() }
        .sortedBy { it.name.orgChartSortKey() }

fun OrgChartData.orphanCompanies(): List<Company> =
    companies
        .filter { company -> !company.inactive && (companyLinks[company.id] ?: emptySet()).isEmpty() }
        .sortedBy { it.name.orgChartSortKey() }

fun OrgChartData.totalsByType(): Map<CompanyType, Int> = companies.filter { !it.inactive }.groupingBy { it.type }.eachCount()

/** Deriva o id numérico de unidade a partir do UUID da company (mesmo esquema de [Company.unitId]). */
fun String.toUnitId(): Int = filter { it.isDigit() }.toIntOrNull() ?: 0

val Company.unitId: Int
    get() = id.toUnitId()

fun OrgChartData.companyRefs(): List<CompanyRef> = companies.filter { !it.inactive }.map { CompanyRef(id = it.unitId, name = it.name) }

/**
 * Chave de unidade usada pelo módulo Operacional. No backend remoto é o `organogramaUnidadeId`
 * (FK de `operational_entries`); nas fontes em memória cai no próprio id da company.
 */
val Company.unidadeKey: String
    get() = organogramaUnidadeId.ifBlank { id }

fun OrgChartData.unidadeKeyOf(ref: CompanyRef): String? = companies.firstOrNull { !it.inactive && it.name.equals(ref.name, ignoreCase = true) }?.unidadeKey

fun OrgChartData.managerNames(): List<String> = managers.filter { !it.inactive }.map { it.name }

fun OrgChartData.directorNames(): List<String> = directors.filter { !it.inactive }.map { it.name }

/** Gestores vinculados a alguma das unidades informadas (pelo nome). */
fun OrgChartData.managerNamesLinkedTo(companyNames: Set<String>): List<String> {
    val companyIds = companies.filter { !it.inactive && it.name in companyNames }.map { it.id }.toSet()
    val managerIds = companyIds.flatMap { companyLinks[it].orEmpty() }.mapNotNull { it.managerId }.toSet()
    return managers.filter { !it.inactive && it.id in managerIds }.map { it.name }
}

/** Diretores vinculados a alguma das unidades informadas (pelo nome). */
fun OrgChartData.directorNamesLinkedTo(companyNames: Set<String>): List<String> {
    val companyIds = companies.filter { !it.inactive && it.name in companyNames }.map { it.id }.toSet()
    val directorIds =
        companyIds
            .flatMap { companyLinks[it].orEmpty() }
            .map { it.directorId }
            .filter { it.isNotBlank() }
            .toSet()
    return directors.filter { !it.inactive && it.id in directorIds }.map { it.name }
}

fun OrgChartData.responsibleNames(companyName: String): Pair<List<String>, List<String>> {
    val company = companies.firstOrNull { it.name.equals(companyName, ignoreCase = true) } ?: return emptyList<String>() to emptyList()
    val links = companyLinks[company.id].orEmpty()
    val directorIds = links.mapNotNull { it.directorId }.toSet()
    val managerIds = links.mapNotNull { it.managerId }.toSet()
    val directorNames = directors.filter { it.id in directorIds }.map { it.name }
    val managerNames = managers.filter { it.id in managerIds }.map { it.name }
    return directorNames to managerNames
}

fun List<Company>.adminOthersPercentFor(companyName: String): Double =
    firstOrNull { !it.inactive && it.name.equals(companyName, ignoreCase = true) }?.adminOthersPercent
        ?: firstOrNull { it.name.equals(companyName, ignoreCase = true) }?.adminOthersPercent
        ?: DEFAULT_ADMIN_OTHERS_PERCENT
